import {
  useCallback, useEffect, useRef, useState, useMemo,
} from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';
import { usePromiseCounterActionContext } from '@bingads-webui-react/counter-context';
import {
  IAsyncToken, IUsePromiseOptions, IUsePromiseCallbackState, IUsePromiseCallbackResult,
} from './types';

const DefaultOptions: IUsePromiseOptions = {
  manual: true,
};

const LoadingState = {
  loading: true,
};

const useIsDependenciesChanged = (dependencies: any[]): boolean => {
  const isDependenciesChangedRef = useRef(false);
  isDependenciesChangedRef.current = false;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => { isDependenciesChangedRef.current = true; }, dependencies);

  return isDependenciesChangedRef.current;
};

/**
 * Hook that facilitates the consumption and management of a promise.
 * Takes care of clearing up the handlers for the callback on unmount
 * or whenever the function that builds the promise needs to re-execute.
 * @param {function} getPromise Function that returns a promise, which lifecycle state
 * will be managed by the hook. Call 'execute' when the data promise needs to be
 * started.
 * @param {Array} dependencies Controls the memoization of the 'execute' function.
 * The identity of 'execute' needs to be stable so it can be used as part of
 * a effect or callback dependency.
 * @param {object} options An options object.
 * - manual(true) - set to false if the request is executed immediately.
 * Useful for requests that should not be executed when the component renders.
 * It impacts the initial loading state to be already true or false until the request is triggered
 * @returns {object} Object containing the following props:
 * - data: contains the resolved value for the promise.
 * - loading: keeps track of the promise state (true when pending, false otherwise)
 * - error: contains the rejected value in case the promise threw one
 * - execute: function that controls when the promise callback is fired.
 */
export const usePromiseCallback = <T>(getPromise: () => Promise<T>, dependencies: any[], options: IUsePromiseOptions = DefaultOptions): IUsePromiseCallbackResult<T> => {
  const { increment, decrement } = usePromiseCounterActionContext();
  const memoizedGetPromise = useCallback(getPromise, dependencies);

  const [state, setState] = useState<IUsePromiseCallbackState<T>>({
    loading: !options.manual,
  });

  const asyncTokenRef = useRef<IAsyncToken<T> | null>(null);

  const cancelTokenIfInFlight = () => asyncTokenRef.current && asyncTokenRef.current.cancel();

  const execute = useCallback(({ manipulatePromiseCounter = false }: { manipulatePromiseCounter?: boolean } = {}): Promise<T> =>
    new Promise((resolve, reject) => {
      const incrementPromiseCount = manipulatePromiseCounter && _.isFunction(increment)
        ? increment : _.noop;
      const decrementPromiseCount = manipulatePromiseCounter && _.isFunction(decrement)
        ? decrement : _.noop;

      cancelTokenIfInFlight();

      setState(LoadingState);

      incrementPromiseCount();

      asyncTokenRef.current = createAsyncToken(memoizedGetPromise()) as unknown as IAsyncToken<T>;

      asyncTokenRef.current.on({
        onSuccess: (data: T) => {
          setState({
            loading: false,
            data,
          });
          decrementPromiseCount();
          resolve(data);
        },
        onError: (error: any) => {
          setState({
            loading: false,
            error,
          });
          decrementPromiseCount();
          reject(error);
        },
        onCancel: () => {
          decrementPromiseCount();
          const abortError = new Error('Promise cancelled');
          abortError.name = 'AbortError';
          reject(abortError);
        },
      });
    }), [memoizedGetPromise, increment, decrement]);

  useEffect(() => cancelTokenIfInFlight, []);

  const isDependenciesChanged = useIsDependenciesChanged(dependencies);

  return {
    ...(isDependenciesChanged && !options.manual ? LoadingState : state),
    execute,
  };
};
